// Iter 204i — Terminal 2.0 chrome.  Mounts at the app root and renders
// the command bar + ticker rail + bottom status bar ONLY when the user
// has flipped into Terminal mode.  In Classic mode this component
// renders nothing and the existing TopNav / Footer continue to work.
//
// Strategy: terminal chrome is FIXED to the viewport (top+bottom) and
// the page body is left untouched; CSS overrides in index.css push
// page content down by the chrome height when `.terminal-mode` is on
// the <html> element.
import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  Search, Terminal, TrendingUp, TrendingDown, Radio, Activity, ChevronDown,
  Settings,
} from "lucide-react";
import axios from "axios";
import { useDesignMode } from "../../context/DesignModeContext";
import { useTheme } from "../../context/ThemeContext";
import { useAuth } from "../../context/AuthContext";
import DesignModeToggle from "./DesignModeToggle";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TICKER_REFRESH_MS = 30_000;

// Primary nav — consolidated to 4 groups + Dashboard.  Mirrors the
// classic top-nav so users get the same mental model in both modes.
const NAV_LIVE = [
  { to: "/live",    label: "Live matches" },
  { to: "/live-ai", label: "Live AI" },
];
const NAV_PREDICT = [
  { to: "/coach",            label: "Bet Coach" },
  { to: "/account/betslips", label: "Daily Slips" },
];
const NAV_MARKETS = [
  { to: "/halftime",       label: "Halftime" },
  { to: "/double-chance",  label: "Double Chance" },
  { to: "/asian-handicap", label: "Asian Handicap" },
  { to: "/goal-timing",    label: "Goal Timing" },
  { to: "/big-leagues",    label: "Big Leagues Premium" },
];
const NAV_COMPETE = [
  { to: "/world-cup",   label: "World Cup" },
  { to: "/tournaments", label: "Tournaments" },
  { to: "/my-teams",    label: "My Teams" },
];
const NAV_PERFORMANCE = [
  { to: "/accuracy",                    label: "Accuracy" },
  { to: "/history",                     label: "History" },
  { to: "/analytics/confidence-trend",  label: "Confidence Trend" },
];


function NavLink({ to, label, isActive }) {
  return (
    <Link
      to={to}
      data-testid={`terminal-nav-${label.toLowerCase().replace(/\s+/g, "-")}`}
      className="text-[11px] font-medium px-2 py-1 rounded-md transition-colors whitespace-nowrap"
      style={{
        color: isActive ? "var(--terminal-accent-amber)" : "var(--terminal-fg-strong)",
        background: isActive ? "rgba(245, 165, 36, 0.10)" : "transparent",
      }}
    >
      {label}
    </Link>
  );
}


function GroupNav({ label, items, pathname, subGroup = null, testId }) {
  const [open, setOpen] = useState(false);
  const [subOpen, setSubOpen] = useState(false);
  const isActive =
    items.some((m) => pathname.startsWith(m.to)) ||
    (subGroup && subGroup.items.some((m) => pathname.startsWith(m.to)));

  useEffect(() => {
    if (!open) return;
    const close = () => { setOpen(false); setSubOpen(false); };
    window.addEventListener("click", close);
    return () => window.removeEventListener("click", close);
  }, [open]);

  const subActive = subGroup && subGroup.items.some((m) => pathname.startsWith(m.to));

  return (
    <div className="relative">
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); setOpen((v) => !v); setSubOpen(false); }}
        data-testid={testId}
        className="text-[11px] font-medium px-2 py-1 rounded-md flex items-center gap-1 whitespace-nowrap"
        style={{
          color: isActive ? "var(--terminal-accent-amber)" : "var(--terminal-fg-strong)",
          background: isActive ? "rgba(245, 165, 36, 0.10)" : "transparent",
        }}
      >
        {label}
        <ChevronDown className="w-3 h-3" />
      </button>
      {open && (
        <div
          onClick={(e) => e.stopPropagation()}
          className="absolute top-full left-0 mt-1 min-w-[200px] rounded-lg overflow-visible"
          style={{
            background: "var(--terminal-panel-strong)",
            border: "1px solid var(--terminal-grid-strong)",
            boxShadow: "0 16px 48px -16px rgba(0, 0, 0, 0.5)",
            zIndex: 60,
          }}
          data-testid={`${testId}-menu`}
        >
          {items.map((m) => (
            <Link
              key={m.to}
              to={m.to}
              onClick={() => setOpen(false)}
              className="block px-3 py-2 text-[11px] font-medium transition-colors hover:bg-white/5"
              style={{
                color: pathname === m.to ? "var(--terminal-accent-amber)" : "var(--terminal-fg-strong)",
              }}
              data-testid={`${testId}-${m.label.toLowerCase().replace(/\s+/g, "-")}`}
            >
              {m.label}
            </Link>
          ))}
          {subGroup && (
            <div
              className="relative"
              onMouseEnter={() => setSubOpen(true)}
              onMouseLeave={() => setSubOpen(false)}
            >
              <div
                className="flex items-center justify-between px-3 py-2 text-[11px] font-medium cursor-pointer border-t"
                style={{
                  color: subActive ? "var(--terminal-accent-amber)" : "var(--terminal-fg-strong)",
                  background: subActive ? "rgba(245, 165, 36, 0.06)" : "transparent",
                  borderColor: "var(--terminal-grid-strong)",
                }}
                data-testid={`${testId}-sub-${subGroup.label.toLowerCase()}`}
              >
                {subGroup.label}
                <ChevronDown className="w-3 h-3 -rotate-90" />
              </div>
              {subOpen && (
                <div
                  className="absolute top-0 left-full ml-1 min-w-[200px] rounded-lg overflow-hidden"
                  style={{
                    background: "var(--terminal-panel-strong)",
                    border: "1px solid var(--terminal-grid-strong)",
                    boxShadow: "0 16px 48px -16px rgba(0, 0, 0, 0.5)",
                  }}
                >
                  {subGroup.items.map((m) => (
                    <Link
                      key={m.to}
                      to={m.to}
                      onClick={() => { setOpen(false); setSubOpen(false); }}
                      className="block px-3 py-2 text-[11px] font-medium transition-colors hover:bg-white/5"
                      style={{
                        color: pathname === m.to ? "var(--terminal-accent-amber)" : "var(--terminal-fg-strong)",
                      }}
                      data-testid={`${testId}-sub-${m.label.toLowerCase().replace(/\s+/g, "-")}`}
                    >
                      {m.label}
                    </Link>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}


function CommandBar() {
  const themeCtx = useTheme() || {};
  const { theme = "dark" } = themeCtx;
  const toggleTheme = themeCtx.toggle || themeCtx.toggleTheme || (() => {});
  const authCtx = useAuth() || {};
  const { user, logout = () => {} } = authCtx;
  const { pathname } = useLocation();
  const [query, setQuery] = useState("");

  // "/" keyboard shortcut — focuses the command search input.
  useEffect(() => {
    const onKey = (e) => {
      if (e.target?.tagName === "INPUT" || e.target?.tagName === "TEXTAREA") return;
      if (e.key === "/") {
        e.preventDefault();
        const el = document.getElementById("nd-terminal-cmd");
        el && el.focus();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const submit = (e) => {
    e.preventDefault();
    if (query.trim()) window.location.href = `/search?q=${encodeURIComponent(query.trim())}`;
  };

  return (
    <header
      data-testid="terminal-command-bar"
      className="fixed top-0 left-0 right-0 z-50 backdrop-blur-xl"
      style={{
        background: "var(--terminal-panel-strong)",
        borderBottom: "1px solid var(--terminal-grid-strong)",
        color: "var(--terminal-fg-strong)",
        boxShadow: "0 12px 32px -16px rgba(0, 0, 0, 0.35)",
      }}
    >
      {/* Row 1 — Brand + search + right cluster */}
      <div className="h-14 flex items-center gap-3 px-3 sm:px-4">
      {/* Brand */}
      <Link to="/" className="flex items-center gap-2 shrink-0">
        <img
          src="/ndakubet-logo.jpg"
          alt="NDAKUBET"
          className="w-8 h-8 rounded-lg object-cover"
          style={{ boxShadow: "0 6px 16px -8px rgba(245, 165, 36, 0.5)" }}
        />
        <span className="font-heading text-base font-semibold tracking-tight hidden sm:inline">
          NDAKUBET
        </span>
        <span
          className="text-[9px] font-mono font-bold px-1.5 py-0.5 rounded-md"
          style={{
            background: "rgba(245, 165, 36, 0.12)",
            color: "var(--terminal-accent-amber)",
          }}
        >
          AI 2.0
        </span>
      </Link>

      {/* Centered command search */}
      <form onSubmit={submit} className="flex-1 max-w-2xl mx-auto">
        <div
          className="relative flex items-center rounded-xl border transition-colors focus-within:border-[var(--terminal-accent-amber)]"
          style={{
            borderColor: "var(--terminal-grid-strong)",
            background: "rgba(0, 0, 0, 0.24)",
          }}
        >
          <Search className="w-3.5 h-3.5 ml-3 text-[var(--terminal-neutral-strong)]" />
          <input
            id="nd-terminal-cmd"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search team · league · country — press / to focus"
            className="flex-1 bg-transparent px-2.5 py-2 text-sm placeholder:text-[var(--terminal-neutral-strong)] focus:outline-none"
            data-testid="terminal-command-input"
            style={{ color: "var(--terminal-fg-strong)" }}
          />
          <kbd
            className="hidden sm:inline-block text-[10px] font-mono mr-2 px-1.5 py-0.5 rounded-md"
            style={{
              background: "rgba(255, 255, 255, 0.04)",
              color: "var(--terminal-neutral-strong)",
              border: "1px solid var(--terminal-grid-strong)",
            }}
          >
            /
          </kbd>
        </div>
      </form>

      {/* Right cluster */}
      <div className="flex items-center gap-2 shrink-0">
        <span
          className="hidden md:inline-flex items-center gap-1.5 text-[10px] tracking-wider px-2.5 py-1 rounded-full"
          style={{
            background: "rgba(34, 211, 238, 0.10)",
            color: "var(--terminal-accent-cyan)",
            border: "1px solid rgba(34, 211, 238, 0.30)",
          }}
          title="Both data providers active"
        >
          <Radio className="w-3 h-3 animate-pulse" />
          Dual-feed live
        </span>
        <button
          onClick={toggleTheme}
          aria-label="Toggle light/dark theme"
          className="px-2.5 py-1 rounded-full text-[10px] uppercase tracking-wider"
          style={{
            background: "rgba(255, 255, 255, 0.04)",
            border: "1px solid var(--terminal-grid-strong)",
            color: "var(--terminal-fg-strong)",
          }}
          data-testid="terminal-theme-toggle"
        >
          {theme === "dark" ? "Dark" : "Light"}
        </button>
        <DesignModeToggle />
        {user ? (
          <>
            <Link
              to="/account"
              aria-label="Account & Settings"
              title="Account & Settings"
              data-testid="terminal-settings-link"
              className="px-2.5 py-1 rounded-full inline-flex items-center gap-1 text-[10px] uppercase tracking-wider"
              style={{
                background: "rgba(255, 255, 255, 0.04)",
                border: "1px solid var(--terminal-grid-strong)",
                color: "var(--terminal-fg-strong)",
              }}
            >
              <Settings className="w-3.5 h-3.5" />
              <span className="hidden md:inline">Settings</span>
            </Link>
            <button
              onClick={logout}
              className="text-[10px] uppercase tracking-wider px-2.5 py-1 rounded-full"
              style={{
                background: "rgba(255, 255, 255, 0.04)",
                border: "1px solid var(--terminal-grid-strong)",
                color: "var(--terminal-fg-strong)",
              }}
            >
              Logout
            </button>
          </>
        ) : (
          <Link
            to="/auth"
            className="text-[10px] uppercase tracking-wider px-2.5 py-1 rounded-full"
            style={{
              background: "rgba(255, 255, 255, 0.04)",
              border: "1px solid var(--terminal-grid-strong)",
              color: "var(--terminal-fg-strong)",
            }}
          >
            Login
          </Link>
        )}
      </div>
      </div>

      {/* Row 2 — Consolidated primary navigation */}
      <nav
        className="h-9 flex items-center gap-1 px-3 sm:px-4 relative"
        style={{ borderTop: "1px solid var(--terminal-grid-strong)" }}
        data-testid="terminal-nav-row"
      >
        <NavLink to="/" label="Dashboard" isActive={pathname === "/"} />
        <NavLink to="/schedule" label="Schedule" isActive={pathname.startsWith("/schedule")} />
        <GroupNav
          label="Live"
          items={NAV_LIVE}
          pathname={pathname}
          testId="terminal-nav-live"
        />
        <GroupNav
          label="Predict"
          items={NAV_PREDICT}
          subGroup={{ label: "Markets", items: NAV_MARKETS }}
          pathname={pathname}
          testId="terminal-nav-predict"
        />
        <GroupNav
          label="Compete"
          items={NAV_COMPETE}
          pathname={pathname}
          testId="terminal-nav-compete"
        />
        <GroupNav
          label="Performance"
          items={NAV_PERFORMANCE}
          pathname={pathname}
          testId="terminal-nav-performance"
        />
        {(user?.is_gold || user?.is_admin) && (
          <Link
            to="/gold"
            data-testid="terminal-nav-gold"
            className="text-[11px] font-bold px-2 py-1 rounded-md tracking-widest uppercase transition-colors"
            style={{
              background: pathname.startsWith("/gold")
                ? "linear-gradient(135deg, var(--terminal-accent-amber), #d97706)"
                : "rgba(245, 165, 36, 0.10)",
              color: pathname.startsWith("/gold") ? "#000" : "var(--terminal-accent-amber)",
              border: pathname.startsWith("/gold") ? "none" : "1px solid rgba(245, 165, 36, 0.35)",
            }}
          >
            ★ Gold
          </Link>
        )}
        <span className="ml-auto text-[10px] tracking-[0.18em] uppercase hidden md:inline"
              style={{ color: "var(--terminal-neutral-strong)" }}>
          Press <kbd className="font-mono">/</kbd> to search
        </span>
      </nav>
    </header>
  );
}


function TickerRail() {
  const [items, setItems] = useState([]);
  useEffect(() => {
    let cancel = false;
    const fetchOnce = async () => {
      try {
        // Pull gold-finland top picks + AH daily top picks for the ticker.
        const [gf, ah] = await Promise.allSettled([
          axios.get(`${API}/live/next-goal/gold-finland`),
          axios.get(`${API}/asian-handicap/daily`),
        ]);
        const next = [];
        if (gf.status === "fulfilled") {
          (gf.value.data?.gold || []).slice(0, 6).forEach((g) => {
            next.push({
              kind: "gold",
              label: `${g.home_team} ${g.current_score} ${g.away_team}`,
              detail: `FT ${g.exact_scores?.[0]?.score || "?"} · ${g.confidence_pct}%`,
              trend: "up",
            });
          });
        }
        if (ah.status === "fulfilled") {
          (ah.value.data?.picks || []).slice(0, 10).forEach((p) => {
            const hp = p.headline_pick || {};
            next.push({
              kind: "ah",
              label: hp.side === "home" ? p.home_team?.name : p.away_team?.name,
              detail: `AH ${hp.line ?? "?"} · ${Math.round(p.confidence_pct || 0)}%`,
              trend: (p.expected_goal_difference || 0) >= 0 ? "up" : "down",
            });
          });
        }
        if (!cancel) setItems(next);
      } catch { /* ignore */ }
    };
    fetchOnce();
    const t = setInterval(fetchOnce, TICKER_REFRESH_MS);
    return () => { cancel = true; clearInterval(t); };
  }, []);

  // Duplicate the items to make the marquee seamless.
  const loop = items.length ? [...items, ...items] : [];

  return (
    <div
      data-testid="terminal-ticker-rail"
      className="fixed left-0 right-0 z-40 h-8 overflow-hidden border-b"
      style={{
        top: "92px",
        background: "var(--terminal-panel-strong)",
        borderColor: "var(--terminal-grid-strong)",
        color: "var(--terminal-fg-strong)",
      }}
    >
      {loop.length === 0 ? (
        <div className="px-4 py-1.5 text-[10px] tracking-[0.16em] uppercase text-[var(--terminal-neutral)]">
          Ticker · waiting for live signal…
        </div>
      ) : (
        <div className="ticker-track flex items-center gap-6 whitespace-nowrap h-full pl-4">
          {loop.map((it, i) => {
            const Trend = it.trend === "up" ? TrendingUp : TrendingDown;
            const color =
              it.trend === "up" ? "var(--terminal-accent-emerald)"
              : "var(--terminal-accent-rose)";
            return (
              <span key={i} className="inline-flex items-center gap-2 text-[11px]">
                <span
                  className="px-1.5 py-0.5 text-[9px] uppercase tracking-[0.14em] font-semibold rounded-md"
                  style={{
                    background: it.kind === "gold"
                      ? "linear-gradient(135deg, var(--terminal-accent-amber), #d97706)"
                      : "rgba(34, 211, 238, 0.10)",
                    color: it.kind === "gold" ? "#000" : "var(--terminal-accent-cyan)",
                    border: it.kind === "gold"
                      ? "none"
                      : "1px solid rgba(34, 211, 238, 0.30)",
                  }}
                >
                  {it.kind === "gold" ? "Gold" : "AH"}
                </span>
                <Trend className="w-3 h-3" style={{ color }} />
                <span className="text-[var(--terminal-fg)] font-medium">{it.label}</span>
                <span className="font-mono text-[10px]" style={{ color }}>{it.detail}</span>
                <span className="text-[var(--terminal-neutral)]">·</span>
              </span>
            );
          })}
        </div>
      )}
    </div>
  );
}


function StatusBar() {
  const [now, setNow] = useState(new Date());
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, []);
  const utc = now.toISOString().slice(11, 19);
  return (
    <footer
      data-testid="terminal-status-bar"
      className="fixed bottom-0 left-0 right-0 z-40 h-7 flex items-center justify-between px-3 text-[10px] tracking-wider border-t"
      style={{
        background: "var(--terminal-panel-strong)",
        borderColor: "var(--terminal-grid-strong)",
        color: "var(--terminal-neutral-strong)",
      }}
    >
      <span className="inline-flex items-center gap-2">
        <Activity className="w-3 h-3 text-[var(--terminal-accent-emerald)] animate-pulse" />
        <span className="uppercase">UTC</span>
        <span className="text-[var(--terminal-fg-strong)] font-mono">{utc}</span>
      </span>
      <span className="hidden sm:flex items-center gap-4">
        <span className="uppercase">Build <span className="text-[var(--terminal-fg-strong)] font-mono">2.0.204i</span></span>
        <span className="uppercase">Calibration <span style={{ color: "var(--terminal-accent-amber)" }}>self-tuning</span></span>
        <span className="uppercase">Latency <span style={{ color: "var(--terminal-accent-emerald)" }}>ok</span></span>
      </span>
      <span className="uppercase tracking-[0.2em] font-semibold" style={{ color: "var(--terminal-fg-strong)" }}>
        Ndaku · AI Terminal
      </span>
    </footer>
  );
}


export default function TerminalChrome() {
  const { mode } = useDesignMode();
  if (mode !== "terminal") return null;
  return (
    <>
      <CommandBar />
      <TickerRail />
      <StatusBar />
    </>
  );
}
